// Whether what is on screen can vouch for itself: is a connection working, is a
// refresh really still running, and does Maloo have every post of a date window
// on file? Pure; tested in freshness.test.ts.

import { fmtDate } from './format';
import type { AnalyticsPlatform, BrandAnalytics, HeldCoverage, SyncInfo } from './types';

/** The platform no longer honours the connection: someone has to sign in again.
 *  Only the platform's own refusal says so. A 'not_connected' row on a platform
 *  that is connected is stale (the pipeline writes it only when the connection
 *  is missing at run time), and a platform that isn't connected needs a
 *  Connect, not a Reconnect. */
export function reconnectNeeded(sync: SyncInfo | null | undefined): boolean {
  return sync?.status === 'needs_reauth';
}

export type HealthState = 'not_connected' | 'pending' | 'working' | 'failing' | 'reconnect';

export interface ConnectionHealth {
  state: HealthState;
  /** The pipeline's plain-language reason, for failing / reconnect. */
  reason: string | null;
  lastSuccessAt: string | null;
  /** A refresh is running now (isLiveLease). */
  refreshing: boolean;
}

export const NOT_CONNECTED_HEALTH: ConnectionHealth = { state: 'not_connected', reason: null, lastSuccessAt: null, refreshing: false };

/** One rule for both pages, from what the platform last answered
 *  (analytics_sync_state via get_analytics_brand) — never a stored token
 *  expiry: the Facebook one dates a user token nothing reads after connect, and
 *  the platforms' refusals are what actually stop the numbers.
 *  not_connected: brand.connections[p] is null. pending: connected but no sync
 *  row, a stale 'not_connected' row, or 'ok' with no success yet (a pair's first
 *  row starts out 'ok'). working: ok with a last success. failing: 'failed'
 *  (Maloo retries on its own). reconnect: 'needs_reauth'. */
export function connectionHealth(
  platform: AnalyticsPlatform,
  brand: BrandAnalytics,
  readAt: number,
  now: number = Date.now(),
): ConnectionHealth {
  if (!brand.connections[platform]) return NOT_CONNECTED_HEALTH;
  const sync = brand.sync.find((s) => s.platform === platform);
  const facts = { lastSuccessAt: sync?.last_success_at ?? null, refreshing: !!sync && isLiveLease(sync, readAt, now) };
  if (sync?.status === 'needs_reauth') return { state: 'reconnect', reason: sync.reason, ...facts };
  if (sync?.status === 'failed') return { state: 'failing', reason: sync.reason, ...facts };
  if (sync?.status === 'ok' && sync.last_success_at) return { state: 'working', reason: null, ...facts };
  return { state: 'pending', reason: null, ...facts };
}

/** The server stops counting a lease as live 10 minutes after it was taken. */
export const LEASE_TTL_MS = 10 * 60_000;
const DAY_MS = 86_400_000;

/** How old a lease is now: its age by the server's clock when it was read
 *  (`lease_age_s`), plus the time since that read by this browser's clock.
 *  The two clocks are never compared, so a browser running fast or slow
 *  can't make a running refresh look finished, or a dead one look alive.
 *  `readAt` is when the read arrived (react-query's dataUpdatedAt). */
export function leaseAgeMs(s: SyncInfo, readAt: number, now: number = Date.now()): number | null {
  if (!s.refreshing) return null;
  return (s.lease_age_s ?? 0) * 1000 + Math.max(0, now - readAt);
}

/** A lease the page may treat as a refresh in progress. The server's flag is
 *  only as fresh as the read that carried it: when reads start failing, that
 *  read's lease still runs out, or the page would poll — and show
 *  "Refreshing…" with Refresh disabled — for as long as it stays open. */
export function isLiveLease(s: SyncInfo, readAt: number, now: number = Date.now()): boolean {
  const age = leaseAgeMs(s, readAt, now);
  return age !== null && age < LEASE_TTL_MS;
}

/** When, by this browser's clock, the first live lease in a read runs out —
 *  the page re-renders then, whether or not another read comes. */
export function nextLeaseExpiry(sync: SyncInfo[], readAt: number, now: number = Date.now()): number | null {
  const ends = sync.flatMap((s) => {
    const age = leaseAgeMs(s, readAt, now);
    return age !== null && age < LEASE_TTL_MS ? [now + LEASE_TTL_MS - age] : [];
  });
  return ends.length > 0 ? Math.min(...ends) : null;
}

/** Coverage ends at the last successful listing; more than a day ago means
 *  the pair has stopped refreshing, and posts since then may be missing. */
export function stoppedRefreshing(held: HeldCoverage | undefined, now: number = Date.now()): boolean {
  if (!held?.until) return false;
  return Date.parse(held.until) < now - DAY_MS;
}

/** The span every post is on file for, in words: "only since 8 Jun 2026", or
 *  "only from 27 Jan 2025 to 1 Aug 2026" once the pair stopped refreshing.
 *  Null while nothing is fully on file. */
export function heldSpan(held: HeldCoverage | undefined, now: number = Date.now()): string | null {
  if (!held?.since) return null;
  return stoppedRefreshing(held, now)
    ? `only from ${fmtDate(held.since)} to ${fmtDate(held.until)}`
    : `only since ${fmtDate(held.since)}`;
}

/** Why "no posts" can't be taken at its word for one platform: the line
 *  under an empty row, ranking or table whose dates aren't all on file. After
 *  the span's end it says "may be missing", not "aren't on file": a run that
 *  is still retrying a gap does store new posts past the last success. */
export function notOnFileNote(label: string, held: HeldCoverage | undefined, now: number = Date.now()): string {
  const span = heldSpan(held, now);
  if (span === null) return `Maloo doesn't have every ${label} post on file yet, so this doesn't mean none went out.`;
  return stoppedRefreshing(held, now)
    ? `Maloo has every ${label} post ${span}; any others in these dates may be missing.`
    : `Maloo has every ${label} post ${span}; earlier posts in these dates, if any, aren't on file.`;
}
